<template>
  <v-menu offset-y>
    <template v-slot:activator="{ on, attrs }">
      <div v-bind="attrs" class="version-dropdown__trigger" v-on="on">
        <InfoTooltip top small>
          <template #element>
            <div class="spacex-4 version-dropdown__title">
              <Badge color="dark-grey">{{ $t('v') }}{{ offer.name }}</Badge>
              <Badge :color="OFFER_STATUS_BADGE[status]">
                {{ $t(status) }}
              </Badge>
            </div>
          </template>
          <div class="text-sm versions-tooltip">
            <div v-if="offer.status === 'draft'">
              {{
                `${$t('Edited on')} ${prettyDates(offer.updated_at, true)} ${$t(
                  'by'
                )} ${editorName}`
              }}
              <div>{{ `${$t('Created by')} ${creatorName}` }}</div>
            </div>
            <div v-else>
              {{
                `${$t('Created on')} ${prettyDates(offer.created_at, true)} ${$t(
                  'by'
                )} ${creatorName}`
              }}
            </div>
          </div>
        </InfoTooltip>
        <ArrowIcon />
      </div>
    </template>
    <div class="dropdown-body">
      <DropdownMenuElement text-class="primary-600" @click="handleExcelDownload">
        {{ $t('Download as Excel file (.xlsx)') }}
      </DropdownMenuElement>
      <DropdownMenuElement v-if="createNewFromThis" @click="handleCreationFrom">
        {{ $t('Create new from this offer') }}
      </DropdownMenuElement>
      <DropdownMenuElement v-if="isDraft" @click="handleImport">
        {{ $t('Import from Excel file (.xlsx)') }}
      </DropdownMenuElement>
      <DropdownMenuElement
        v-if="isDraft && !lastVersionIsDraft"
        text-class="danger-600"
        @click="handleDelete"
      >
        {{ $t('Delete') }}
      </DropdownMenuElement>
      <DropdownMenuElement v-else text-class="danger-600" @click="handleArchive(!isArchived)">
        {{ isArchived ? $t('Unarchive') : $t('Archive') }}
      </DropdownMenuElement>
    </div>
  </v-menu>
</template>

<script>
  import { VMenu } from 'vuetify/lib';
  import Badge from '@/components/ui/Badge';
  import OFFER_STATUS_BADGE from '@/utils/constants/offer-status-badge';
  import ArrowIcon from '@/assets/icons/chevron-down.svg';
  import DropdownMenuElement from '@/components/ui/dropdown-menu/DropdownMenuElement';
  import get from 'lodash.get';
  import { mapActions, mapGetters } from 'vuex';
  import offerTemplate from '@/utils/mixins/offer-template';
  import downloadByLink from '@/utils/download-by-link';
  import CreateOfferModal from '@/containers/incoming-tender/CreateOfferModal';
  import prettyDates from '@/utils/formatting/pretty-date';
  import InfoTooltip from '@/components/ui/InfoTooltip';

  export default {
    name: 'OfferVersionDropdown',
    components: {
      VMenu,
      ArrowIcon,
      Badge,
      DropdownMenuElement,
      InfoTooltip,
    },
    mixins: [offerTemplate],
    props: {
      offer: {
        type: Object,
        required: true,
      },
    },
    computed: {
      ...mapGetters('incomingTender', ['isAtLeastOneDraft', 'lastVersionIsDraft']),
      status() {
        return get(this.offer, 'status', 'draft');
      },
      createNewFromThis() {
        return ['archived', 'submitted', 'old'].includes(this.status) && !this.isAtLeastOneDraft;
      },
      isDraft() {
        return this.status === 'draft';
      },
      isArchived() {
        return this.status === 'archived';
      },
      creatorName() {
        return get(this.offer, 'author_user.name', '');
      },
      editorName() {
        return get(this.offer, 'last_edit_user.name', '');
      },
    },
    data() {
      return {
        OFFER_STATUS_BADGE,
      };
    },
    methods: {
      prettyDates,
      ...mapActions({
        confirm: 'confirmModal/openModal',
        openModal: 'modal/openModal',
        showAlert: 'alert/showAlert',
        closeAlert: 'alert/closeAlert',
      }),
      ...mapActions('incomingTender', [
        'createIncomingTenderVersionFromOffer',
        'deleteIncomingTenderVersion',
        'archiveOfferVersion',
      ]),
      async handleExcelDownload() {
        const alertId = await this.showAlert({
          text: `${this.$t('Downloading Excel file')}...`,
          loader: true,
          timeout: false,
        });
        const response = await downloadByLink({
          link: `/incoming_tenders/offers/versions/${this.offer.id}/xlsx/`,
        });
        this.closeAlert(alertId);
        if (response instanceof Error) {
          this.showAlert({
            type: 'error',
            text: this.$t('An error occurred during loading.'),
          });
        } else {
          this.showAlert({
            type: 'success',
            text: `${response.fileName || this.$t('File')} ${this.$t('downloaded successfully!')}`,
          });
        }
      },
      handleCreationFrom() {
        this.createIncomingTenderVersionFromOffer(this.offer.id);
      },
      handleImport() {
        this.openModal({
          component: CreateOfferModal,
          context: {
            versionId: this.offer.id,
          },
        });
      },
      handleDelete() {
        this.confirm({
          title: this.$t('Delete offer?'),
          description: this.$t('This action cannot be undone.'),
          actionText: this.$t('Delete'),
          cancelText: this.$t('Return'),
          style: 'danger',
          action: () => {
            this.deleteIncomingTenderVersion(this.offer.id);
          },
        });
      },
      handleArchive(archive = true) {
        if (archive) {
          this.confirm({
            title: this.$t('Archive offer?'),
            description: this.$t('You can unarchive it later.'),
            actionText: this.$t('Archive'),
            cancelText: this.$t('Return'),
            style: 'danger',
            revertButtons: true,
            action: () => {
              this.archiveOfferVersion({
                offerVersionId: this.offer.id,
                archive,
              });
            },
          });
        } else {
          this.archiveOfferVersion({
            offerVersionId: this.offer.id,
            archive,
          });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .version-dropdown__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    align-content: center;

    width: 100%;
  }

  .version-dropdown__title {
    display: flex;
  }

  .versions-tooltip {
    text-align: start;
  }
</style>
